import config from "../../../config/config";
import { authFetch, buildAuthHeaders } from "../../../public/api/httpClient";

const APPLICATION_BASE = `${config.BASE_URL}/membership-apply`;
const RENEWALS_BASE = `${config.BASE_URL}/membership-renew`;

const getMembershipsBase = () => {
  if (typeof window !== "undefined") {
    if (window.location.pathname.includes("membership-renew")) {
      return RENEWALS_BASE;
    }
  }
  return APPLICATION_BASE;
};

const getPaymentsBase = () => `${getMembershipsBase()}/payments`;

async function parseJson(res) {
  const text = await res.text();
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    return { message: text || "Invalid response" };
  }
}

export const searchStudentsForMembership = async ({
  q = "",
  id = "",
  phone = "",
  name = "",
  limit = 20,
} = {}) => {
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  if (id) params.set("id", id);
  if (phone) params.set("phone", phone);
  if (name) params.set("name", name);
  params.set("limit", String(limit));
  const res = await authFetch(`${getMembershipsBase()}/search/students?${params.toString()}`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(data.message || "Failed to search students");
  return data;
};

export const getStudentMembershipDetails = async (studentId) => {
  const res = await authFetch(`${getMembershipsBase()}/students/${encodeURIComponent(String(studentId))}`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(data.message || "Failed to fetch student details");
  return data;
};

export const getMembershipPlans = async () => {
  const res = await authFetch(`${getMembershipsBase()}/plans`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(data.message || "Failed to fetch membership plans");
  return data;
};

export const submitMembershipApplication = async (payload, csrfToken) => {
  try {
    const res = await authFetch(`${getMembershipsBase()}/apply`, {
      method: "POST",
      credentials: "include",
      headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
      body: JSON.stringify(payload),
    });
    const data = await parseJson(res);
    if (!res.ok) {
      return {
        data: null,
        error: {
          message: data.message || "Failed to submit membership application",
          details: data.details,
        },
      };
    }
    return { data, error: null };
  } catch (error) {
    return {
      data: null,
      error: { message: error.message || "Failed to submit membership application" },
    };
  }
};

export const submitMembershipRenewal = async (payload, csrfToken) => {
  try {
    const res = await authFetch(`${getMembershipsBase()}/renew`, {
      method: "POST",
      credentials: "include",
      headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
      body: JSON.stringify(payload),
    });
    const data = await parseJson(res);
    if (!res.ok) {
      return {
        data: null,
        error: {
          message: data.message || "Failed to submit renewal",
          details: data.details,
        },
      };
    }
    return { data, error: null };
  } catch (error) {
    return {
      data: null,
      error: { message: error.message || "Failed to submit renewal" },
    };
  }
};


/**
 * Get payment modes (meta data including payment modes and sub types)
 * @param {boolean} activeOnly - Only fetch active payment modes
 */
export const getPaymentMeta = async (activeOnly = false) => {
  const q = activeOnly ? "?active_only=true" : "";
  const res = await authFetch(`${getPaymentsBase()}/meta${q}`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) {
    throw new Error(data.message || "Failed to fetch payment meta");
  }
  return data;
};

/**
 * Get payment sub types by payment mode
 * @param {number} modeId - Payment mode ID
 */
export const getPaymentSubTypesByMode = async (modeId) => {
  const res = await authFetch(
    `${getPaymentsBase()}/sub-types?payment_mode_id=${encodeURIComponent(String(modeId))}&active_only=true`,
    {
      credentials: "include",
      headers: buildAuthHeaders(),
    },
  );
  const data = await parseJson(res);
  if (!res.ok) {
    throw new Error(data.message || "Failed to fetch payment sub-types");
  }
  return data;
};

export const getVolunteerMembershipsList = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.student_search) params.append("student_search", filters.student_search);
  if (filters.membership_id) params.append("membership_id", String(filters.membership_id));
  if (filters.application_verified !== undefined && filters.application_verified !== "") {
    params.append("application_verified", String(filters.application_verified));
  }
  if (filters.status_id) params.append("status_id", String(filters.status_id));
  if (filters.date_from) params.append("date_from", filters.date_from);
  if (filters.date_to) params.append("date_to", filters.date_to);
  if (filters.sort_by) params.append("sort_by", filters.sort_by);
  if (filters.order) params.append("order", filters.order);
  if (filters.page) params.append("page", String(filters.page));
  if (filters.limit) params.append("limit", String(filters.limit));
  if (filters.ended_on_or_before_today !== undefined && filters.ended_on_or_before_today !== "") {
    params.append("ended_on_or_before_today", String(filters.ended_on_or_before_today));
  }

  const q = params.toString();
  const res = await authFetch(`${getMembershipsBase()}/applications-list${q ? `?${q}` : ""}`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(data.message || "Failed to fetch memberships list");
  return data;
};

export const getVolunteerRenewalsList = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.student_search) params.append("student_search", filters.student_search);
  if (filters.membership_id) params.append("membership_id", String(filters.membership_id));
  if (filters.renewal_verified !== undefined && filters.renewal_verified !== "") {
    params.append("renewal_verified", String(filters.renewal_verified));
  }
  if (filters.status_id) params.append("status_id", String(filters.status_id));
  if (filters.sort_by) params.append("sort_by", filters.sort_by);
  if (filters.order) params.append("order", filters.order);
  if (filters.ended_on_or_before_today !== undefined && filters.ended_on_or_before_today !== "") {
    params.append("ended_on_or_before_today", String(filters.ended_on_or_before_today));
  }

  const q = params.toString();
  const res = await authFetch(`${getMembershipsBase()}/renewals-list${q ? `?${q}` : ""}`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(data.message || "Failed to fetch renewals list");
  return data;
};

export const getVolunteerRenewalStats = async () => {
  const res = await authFetch(`${getMembershipsBase()}/renewals-stats`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(data.message || "Failed to fetch renewal stats");
  return data;
};

export const updateVolunteerMembershipApplication = async (applicationId, payload, csrfToken) => {
  const res = await authFetch(
    `${getMembershipsBase()}/applications/${encodeURIComponent(String(applicationId))}`,
    {
      method: "PATCH",
      credentials: "include",
      headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
      body: JSON.stringify(payload),
    },
  );
  const data = await parseJson(res);
  if (!res.ok) {
    const err = new Error(data.message || "Failed to update membership application");
    err.details = data.details;
    throw err;
  }
  return data;
};

